// CTW-DURMAH-STUDENT-PAINPOINTS-SEO-RESOURCE-EXPANSION-03:
// Independent learning cluster article: making sense of legal language.
// Deliberately complementary to /learn/how-to-learn-legal-terms (which is
// the memory-science guide): this page is the DECODING on-ramp: what kinds
// of language a new student meets, why legal English looks like this, and
// how to read past it, with the learn article linked for making terms
// stick. Natural, truthful Lexicon connection; no term counts claimed.

import type { ResourceSection, ResourceSourceGroup } from "./types";

export const makingSenseOfLegalLanguageSections: ResourceSection[] = [
  {
    id: "the-foreign-language-feeling",
    heading: "The foreign language feeling",
    paragraphs: [
      "This guide mainly uses England and Wales terminology. Scotland and Northern Ireland have separate legal systems, so record the jurisdiction alongside each definition. In Scottish civil proceedings, for example, the pursuer sues the defender; claimant and defendant are not universal UK labels.",
      "In the first weeks of a law degree, an ordinary paragraph of reading can contain a Latin phrase, a French word dressed as English, an abbreviation you cannot expand, and a familiar English word that clearly does not mean what it usually means. Students describe it accurately as trying to study in a language they half know, and it is one of the quietest sources of first-term overwhelm.",
      "Two things are worth knowing straight away. First, this is a universal experience, not a gap in your particular education; nobody arrives fluent. Second, it is temporary in a specific way: legal vocabulary is a finite working set, most of it front-loaded into the first year, and it yields to method faster than it yields to worry. This page is the method.",
    ],
  },
  {
    id: "why-legal-english-looks-like-this",
    heading: "Why legal English looks like this",
    paragraphs: [
      "Legal English brings together ordinary English, older legal expressions and technical vocabulary. Latin phrases such as ratio decidendi, obiter dicta and mens rea remain in use; Latin was also the language of many historical English records. Expressions such as tort and estoppel need their own legal definitions. Pairs such as null and void are best understood in context rather than treated as two separate doctrines. Focus on the meaning of each expression in the source you are reading.",
      "None of this requires you to study Latin or French. Each surviving phrase is best treated as a single vocabulary item with a plain-English meaning, learned once, like any other technical term. Knowing why the layers exist simply removes the intimidation: the language is old, not clever, and it was not designed to exclude you, even when it feels that way.",
    ],
  },
  {
    id: "the-four-kinds-of-difficult",
    heading: "The four kinds of difficult",
    paragraphs: [
      "Almost everything that makes legal reading hard for a new student falls into four buckets, and naming the bucket is half the fix:",
    ],
    bullets: [
      "Latin and French survivals: ratio decidendi, obiter dicta, mens rea, actus reus, estoppel. Finite in number, learned as single terms.",
      "Ordinary words wearing technical meanings: consideration in contract, nuisance in tort, reasonable nearly everywhere. The most dangerous bucket, because you do not notice you have misunderstood.",
      "The vocabulary of courts and procedure: claimant and defendant in England and Wales civil cases, pursuer and defender in Scottish civil cases, appellant and respondent on appeal. Older reports may use different labels. Identify each person's role rather than assuming the vocabulary is universal.",
      "Citations and abbreviations as a language of their own: what [2019] UKSC 41 encodes, what the report abbreviations mean, why paragraph numbers sit in square brackets.",
    ],
    links: [
      {
        label: "Judiciary of England and Wales: forms of address for judges",
        href: "https://www.judiciary.uk/guidance-and-resources/what-do-i-call-a-judge/",
      },
    ],
    callout: {
      title: "The trap is bucket two",
      body: "A Latin phrase announces itself as foreign, so you look it up. An ordinary English word used as a term of art does not, so you read on, quietly wrong. When a familiar word seems load-bearing in a legal sentence, check whether the law has given it a private meaning.",
    },
  },
  {
    id: "terms-of-art",
    heading: "Terms of art: when English is the foreign language",
    paragraphs: [
      "A term of art is a word or phrase with a technical meaning in a particular legal context. Consideration in English contract law is not ordinary thoughtfulness: look up the legal definition and the relevant doctrine. Malice does not mean the same thing in every legal context. Reasonable is a legal standard whose meaning depends on the rule being applied. Check the jurisdiction and subject, not just the familiar word.",
      "The working habit: whenever a definition seems to matter to the argument you are reading, treat the word as a suspect. Look it up in a legal source rather than a general dictionary, note the legal meaning in your own words, and record which area of law that meaning belongs to, because the same word can carry different technical meanings in different corners of the subject.",
    ],
  },
  {
    id: "where-to-look-things-up",
    heading: "Where to look things up",
    paragraphs: [
      "You need two lookup layers. For quick orientation, use a reputable glossary or your textbook's definitions. The Civil Procedure Rules glossary concerns England and Wales; the Inner Temple Library glossary helps with court and citation terminology. For Scotland, consult the Judiciary of Scotland glossary, and use Northern Irish sources for that jurisdiction. For written work, check the precise definition against the relevant authority or a law dictionary through your library.",
      "For study, the lookup is only step one; the value is in what you do next. Durmah's Legal Lexicon exists for exactly this: plain-English explanations of the terms law students actually meet, with audio for the ones nobody wants to mispronounce in a seminar, and the ability to save the terms from your own lectures as you meet them. Whatever tool you use, the principle is the same: capture the term where you met it, in words you chose, or the lookup evaporates by the weekend.",
    ],
    links: [
      {
        label: "Civil Procedure Rules glossary: England and Wales",
        href: "https://www.justice.gov.uk/courts/procedure-rules/civil/glossary",
      },
      {
        label: "Inner Temple Library: glossary of legal terms and phrases",
        href: "https://www.innertemplelibrary.org.uk/research-and-training/glossary/",
      },
      {
        label: "Judiciary of Scotland: glossary, including pursuer and precedent",
        href: "https://www.judiciary.scot/home/media-information/glossary/p",
      },
    ],
  },
  {
    id: "decoding-as-you-read",
    heading: "Decoding as you read, without stopping every sentence",
    paragraphs: [
      "The instinct to stop at every unknown word is understandable and ruinous: it turns a twenty-page judgment into a vocabulary exercise and destroys any sense of the argument. Read in layers instead, exactly as our case reading guide teaches. On the first pass, let unknown terms wash past; mark them and keep moving, because the shape of the argument is the prize. On the second pass, look up only the terms that turned out to be load-bearing, the ones the reasoning actually pivots on.",
      "You will find that many marked terms never need looking up at all; context answered them, or they were furniture. The handful that remain are your real vocabulary for that topic, and they are precisely the terms worth capturing properly in your own glossary.",
    ],
  },
  {
    id: "saying-it-out-loud",
    heading: "Saying it out loud",
    paragraphs: [
      "Vocabulary you can read but not say is only half-owned, and it fails you at exactly the wrong moments: the seminar answer you did not volunteer because a word in it felt unsayable, the moot submission that wobbled on a case name. Pronunciation anxiety around Latin and unfamiliar names is common and almost never talked about.",
      "The fix is unglamorous: say the terms aloud, early, in private, until they stop being events. Legal Latin pronunciation varies in practice and nobody serious is grading your vowels; what matters is saying the term with enough confidence that your point survives it. Hearing terms spoken helps too, which is why the Lexicon's audio exists. Speaking law is a skill Durmah takes seriously in its own right, and it starts with single words said without flinching.",
    ],
  },
  {
    id: "making-them-stick",
    heading: "Making the terms stick",
    paragraphs: [
      "Decoding gets you through the reading; memory turns vocabulary into fluency, and memory has its own method: testing yourself instead of re-reading, spacing practice across days, and explaining terms in your own words. That science, and how to apply it to legal vocabulary specifically, has its own dedicated guide in our library, and this page will not duplicate it.",
      "The one-line version: a term looked up once is rented; a term retrieved from memory three times across a week is owned. Build the habit of small daily retrieval early in first term, while the vocabulary load is at its peak, and the language problem largely dissolves by the end of it.",
    ],
  },
  {
    id: "common-mistakes",
    heading: "Common mistakes, so you can skip them",
    paragraphs: ["The recurring language mistakes of the first year are all avoidable:"],
    bullets: [
      "Sailing past a familiar word that the law has redefined, and building notes on the wrong meaning.",
      "Stopping at every unknown term on the first pass, and never reaching the argument.",
      "Copying textbook definitions verbatim into notes, which stores the words and discards the meaning.",
      "Using a general dictionary for a term of art in written work.",
      "Never saying terms aloud, then meeting them for the first time out loud in an assessment.",
      "Treating the vocabulary as endless when it is front-loaded and finite.",
      "Leaving terminology to revision, when the spacing that makes it stick needs term time.",
    ],
  },
  {
    id: "where-durmah-fits",
    heading: "Where Durmah fits",
    paragraphs: [
      "Language is one of the places Durmah helps most directly. The Legal Lexicon gives you plain-English explanations and audio for the terms law students actually meet; the study companion can explain a term in the context of the very lecture or case where you met it, which is where definitions actually make sense; and saving your own terms builds the personal glossary this page keeps recommending.",
      "What stays yours is the understanding. A definition delivered is not a concept owned, which is why Durmah explains and quizzes rather than doing your reading for you. The vocabulary is the entry fee to thinking like a lawyer; the thinking is the point.",
    ],
  },
];

export const makingSenseOfLegalLanguageSources: ResourceSourceGroup[] = [
  {
    topic: "Historical language and jurisdiction",
    sources: [
      { label: "The National Archives: medieval Latin records", href: "https://www.nationalarchives.gov.uk/latin/" },
      { label: "Judiciary of Scotland: glossary", href: "https://www.judiciary.scot/home/media-information/glossary/p" },
      { label: "Judiciary NI: judicial decisions and directions", href: "https://www.judiciaryni.uk/judicial-decisions" },
    ],
  },
  {
    topic: "Free glossaries and official guidance",
    sources: [
      {
        label: "Civil Procedure Rules glossary: England and Wales",
        href: "https://www.justice.gov.uk/courts/procedure-rules/civil/glossary",
      },
      {
        label: "Judiciary of England and Wales: forms of address for judges",
        href: "https://www.judiciary.uk/guidance-and-resources/what-do-i-call-a-judge/",
      },
      {
        label: "Inner Temple Library glossary (an Inn of Court library resource)",
        href: "https://www.innertemplelibrary.org.uk/research-and-training/glossary/",
      },
    ],
  },
  {
    topic: "Primary sources where the language lives",
    sources: [
      { label: "Find Case Law: England and Wales decisions plus UK Supreme Court and Privy Council", href: "https://caselaw.nationalarchives.gov.uk/" },
      { label: "legislation.gov.uk: official UK legislation", href: "https://www.legislation.gov.uk/" },
    ],
  },
];
